
import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLicenseSidebar from './MasterLicenseSidebar';
import { Toaster } from '@/components/ui/toaster';
import LoadingSpinner from './LoadingSpinner';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { BookOpen, UserCircle } from 'lucide-react';
import { useLogoUrl } from '@/hooks/useLogoUrl';

interface MasterLicenseLayoutProps {
  children: ReactNode;
  isLoading?: boolean;
  onLogout: () => void;
  activeTab?: string;
  userRole?: string;
}

const MasterLicenseLayout: React.FC<MasterLicenseLayoutProps> = ({
  children,
  isLoading = false,
  onLogout,
  activeTab = 'dashboard',
  userRole = 'Master Admin'
}) => {
  const navigate = useNavigate();
  const logoUrl = useLogoUrl ? useLogoUrl() : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="ml-2">Loading...</p>
      </div>
    );
  }

  const handleKnowledgeBaseClick = () => {
    console.log("Navigating to knowledge base");
    navigate('/master/knowledge-base');
  };
  
  const handleProfileClick = () => {
    navigate('/master/profile');
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <MasterLicenseSidebar activeTab={activeTab} />

        <div className="flex-1 flex flex-col bg-gray-50">
          <div className="bg-white shadow-sm p-4 flex justify-between items-center">
            <div className="flex items-center">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt="Company Logo" 
                  className="h-8 w-auto object-contain"
                />
              ) : (
                <h1 className="text-xl font-bold">Master License Portal</h1>
              )}
              <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                {userRole}
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={handleProfileClick}
                className="flex items-center gap-2"
              >
                <UserCircle className="h-5 w-5" />
                Profile
              </Button>
              <Button 
                variant="ghost" 
                onClick={handleKnowledgeBaseClick}
                className="flex items-center gap-2"
              >
                <BookOpen className="h-5 w-5" />
                Knowledge Base
              </Button>
              <Button 
                variant="destructive"
                onClick={onLogout}
              >
                Logout
              </Button>
            </div>
          </div>
          
          <main className="flex-grow px-4 py-6">
            {children}
          </main>
          <Toaster />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default MasterLicenseLayout;
